import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, ClipboardCheck, Lock, Save, X } from 'lucide-react';
import { CHECK_STAGE_LABEL, type CheckStage, type PreflightKey } from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/dateFmt';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';

/**
 * Shortcut modal for editing the pre-employment checklist (DBS, RTW,
 * DVLA, P45) without opening the full PersonEdit page. Loads the person's
 * metadata on open, edits in local state, PUTs to the same endpoint
 * PersonEdit uses (`/api/admin/people/:id/preflight`).
 *
 * Setting any status to `missing` server-side auto-creates a
 * FlagRaised event in the matching category — same as the inline
 * panel in PersonEdit, so the two surfaces stay consistent.
 */

type CheckStatus = 'ok' | 'pending' | 'missing' | 'na';

interface Preflight {
  dbs_status: CheckStatus;
  right_to_work: CheckStatus;
  dvla_check: CheckStatus;
  p45_received: CheckStatus;
  notes?: string;
  updated_at?: string;
}

/** An assigned screening check; its gate is workflow-managed. */
interface ActiveCheck {
  preflight_key: PreflightKey;
  title: string;
  stage: CheckStage;
}

const STATUSES: CheckStatus[] = ['ok', 'pending', 'missing', 'na'];

function labelFor(s: CheckStatus): string {
  if (s === 'ok') return 'OK';
  if (s === 'pending') return 'Pending';
  if (s === 'missing') return 'Missing';
  return 'N/A';
}

export function PersonPreflightModal({
  open,
  personId,
  personName,
  onClose,
}: {
  open: boolean;
  personId: string;
  personName: string;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dbs, setDbs] = useState<CheckStatus>('pending');
  const [rtw, setRtw] = useState<CheckStatus>('pending');
  const [dvla, setDvla] = useState<CheckStatus>('pending');
  const [p45, setP45] = useState<CheckStatus>('pending');
  const [notes, setNotes] = useState('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [checks, setChecks] = useState<ActiveCheck[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const r = await api<{
          metadata: Record<string, unknown>;
          checks?: ActiveCheck[];
        }>(`/api/admin/people/${personId}`);
        if (cancelled) return;
        const pf = (r.metadata?.preflight ?? {}) as Partial<Preflight>;
        setDbs(pf.dbs_status ?? 'pending');
        setRtw(pf.right_to_work ?? 'pending');
        setDvla(pf.dvla_check ?? 'pending');
        setP45(pf.p45_received ?? 'pending');
        setNotes(pf.notes ?? '');
        setUpdatedAt(pf.updated_at ?? null);
        setChecks(r.checks ?? []);
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err instanceof ApiError ? err.message : 'Could not load the checklist',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, personId]);

  // Esc to close, body scroll lock.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, saving]);

  if (!open) return null;

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      const r = await api<{ metadata: Record<string, unknown> }>(
        `/api/admin/people/${personId}/preflight`,
        {
          method: 'PUT',
          body: JSON.stringify({
            dbs_status: dbs,
            right_to_work: rtw,
            dvla_check: dvla,
            p45_received: p45,
            ...(notes.trim() ? { notes: notes.trim() } : {}),
          }),
        },
      );
      toast.success('Pre-employment checklist saved');
      const pf = (r.metadata?.preflight ?? {}) as Partial<Preflight>;
      setUpdatedAt(pf.updated_at ?? new Date().toISOString());
      // A `missing` status auto-emits a FlagRaised event server-side,
      // so the people list's flag count + this person's flag chip
      // both need to resync.
      qc.invalidateQueries({ queryKey: ['people'] });
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'save failed';
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="preflight-modal-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-fg/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <button
        type="button"
        aria-label="Close"
        disabled={saving}
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />
      <div className="aurora-fade-in flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className="flex items-center justify-between gap-3 border-b border-divider/10 px-5 py-3">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-token-sm bg-accent/10 text-accent"
            >
              <ClipboardCheck className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-accent">
                Pre-Employment Checklist
              </div>
              <h2
                id="preflight-modal-title"
                className="truncate text-base font-semibold tracking-tight text-fg"
              >
                {personName}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            className="rounded-token-sm p-1 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          <p className="text-xs text-fg-muted">
            HR-supplied gate checks. Setting any to <em>missing</em>{' '}
            auto-creates a FlagRaised event in the matching category.
          </p>

          {loading ? (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : loadError ? (
            <div className="mt-4 flex items-start gap-2 rounded-token-sm bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{loadError}</span>
            </div>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <CheckRow label="DBS" value={dbs} onChange={setDbs} managedBy={checks.find((c) => c.preflight_key === 'dbs')} />
                <CheckRow label="Right to work" value={rtw} onChange={setRtw} managedBy={checks.find((c) => c.preflight_key === 'right_to_work')} />
                <CheckRow label="DVLA check" value={dvla} onChange={setDvla} managedBy={checks.find((c) => c.preflight_key === 'dvla')} />
                <CheckRow label="P45 or HMRC Starter Checklist" value={p45} onChange={setP45} managedBy={checks.find((c) => c.preflight_key === 'p45')} />
              </div>
              <label className="mt-4 block text-sm">
                <span className="font-medium text-fg-muted">
                  Checklist notes{' '}
                  <span className="font-normal text-fg-dim">(optional)</span>
                </span>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1.5 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
                />
              </label>
              {updatedAt ? (
                <p className="mt-2 text-xs text-fg-dim">
                  Last updated {formatDateTime(updatedAt)}.
                </p>
              ) : null}
              {saveError ? (
                <div className="mt-3 flex items-start gap-2 rounded-token-sm bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{saveError}</span>
                </div>
              ) : null}
            </>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-divider/10 bg-surface-2 px-5 py-3">
          <Button variant="ghost" size="md" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            iconLeft={<Save className="h-4 w-4" />}
            loading={saving}
            disabled={loading || !!loadError}
            onClick={save}
          >
            Save checklist
          </Button>
        </footer>
      </div>
    </div>
  );
}

function CheckRow({
  label,
  value,
  onChange,
  managedBy,
}: {
  label: string;
  value: CheckStatus;
  onChange: (v: CheckStatus) => void;
  /** The assigned screening check that owns this gate, when one exists. */
  managedBy?: ActiveCheck;
}) {
  const colors: Record<CheckStatus, string> = {
    ok: 'border-success/25 bg-success-soft/15 text-success-soft-fg',
    pending: 'border-warning/25 bg-warning-soft/15 text-warning-soft-fg',
    missing: 'border-danger/25 bg-danger-soft/15 text-danger-soft-fg',
    na: 'border-line-strong/20 bg-surface-2 text-fg-muted',
  };

  // Workflow-managed gate: shows the live check stage instead of an
  // editable status. The server ignores manual writes to it anyway.
  if (managedBy) {
    const display: CheckStatus = managedBy.stage === 'verified' ? 'ok' : value;
    return (
      <div className="block text-sm">
        <span className="font-medium text-fg-muted">{label}</span>
        <div
          className={`mt-1.5 flex h-10 w-full items-center gap-2 rounded-token-sm border px-3 text-sm font-medium ${colors[display]}`}
        >
          <Lock className="h-3.5 w-3.5 shrink-0" />
          {CHECK_STAGE_LABEL[managedBy.stage]}
        </div>
        <span className="mt-1 block text-xs text-fg-dim">
          Managed by the {managedBy.title} task; complete it from the checks page.
        </span>
      </div>
    );
  }

  return (
    <label className="block text-sm">
      <span className="font-medium text-fg-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as CheckStatus)}
        className={`mt-1.5 block h-10 w-full rounded-token-sm border px-3 text-sm font-medium shadow-token-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring/30 ${colors[value]}`}
      >
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {labelFor(s)}
          </option>
        ))}
      </select>
    </label>
  );
}
